import React, { useState, useRef } from "react";
import {
  Card,
  Button,
  Form,
  Alert,
  ProgressBar,
  Badge,
  Modal,
  Spinner,
  Tabs,
  Tab,
} from "react-bootstrap";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  uploadBytesResumable,
} from "firebase/storage";
import { storage } from "../../firebase";

const VideoUpload = ({
  propertyId,
  video = null, // Current video object { url, path, filename, size, type, embedUrl }
  onVideoUpdate,
  disabled = false,
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState(
    video?.embedUrl ? "embed" : "upload"
  );
  const [embedUrl, setEmbedUrl] = useState(video?.embedUrl || "");
  const fileInputRef = useRef(null);

  const validateFile = (file) => {
    // Check file type
    if (!file.type.startsWith("video/")) {
      throw new Error("File must be a video");
    }

    // Check file size (max 100MB for videos)
    const maxSize = 100 * 1024 * 1024;
    if (file.size > maxSize) {
      throw new Error("Video size must be less than 100MB");
    }

    return true;
  };

  const validateEmbedUrl = (url) => {
    if (!url || url.trim() === "") {
      throw new Error("Please enter an embed URL");
    }

    // Check if it's a valid embed URL format
    const validPatterns = [
      /youtube\.com\/embed\//i,
      /vimeo\.com\/video\//i,
      /listings\.futurehomephoto\.com/i,
      /^<iframe/i, // Allow iframe tags
    ];

    const isValid = validPatterns.some((pattern) => pattern.test(url));
    if (!isValid && !url.includes("iframe")) {
      throw new Error("Please enter a valid embed URL or iframe code");
    }

    return true;
  };

  const extractEmbedUrl = (input) => {
    // If it's an iframe tag, extract the src
    const iframeMatch = input.match(/src=["']([^"']+)["']/);
    if (iframeMatch) {
      return iframeMatch[1];
    }
    return input;
  };

  const handleEmbedUrlSubmit = () => {
    try {
      validateEmbedUrl(embedUrl);
      const cleanUrl = extractEmbedUrl(embedUrl);

      const videoData = {
        embedUrl: cleanUrl,
        url: cleanUrl, // Use embedUrl as url for compatibility
        type: "embed",
        uploadedAt: new Date().toISOString(),
      };

      onVideoUpdate(videoData);
      setError("");
    } catch (err) {
      setError(err.message);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setError("");
    setUploadProgress(0);

    try {
      validateFile(file);

      // Create unique filename
      const timestamp = Date.now();
      const filename = `${timestamp}_${file.name.replace(
        /[^a-zA-Z0-9.-]/g,
        "_"
      )}`;
      const videoPath = `properties/${propertyId}/video/${filename}`;

      // Create storage reference
      const storageRef = ref(storage, videoPath);

      // Upload file with progress tracking
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => {
          console.error("Upload error:", error);
          setError("Failed to upload video: " + error.message);
          setUploading(false);
        },
        async () => {
          // Upload completed successfully
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

          const videoData = {
            url: downloadURL,
            path: videoPath,
            filename: filename,
            size: file.size,
            type: file.type,
            uploadedAt: new Date().toISOString(),
          };

          onVideoUpdate(videoData);
          setUploading(false);
          setUploadProgress(0);
        }
      );
    } catch (err) {
      setError(err.message || "Failed to upload video");
      setUploading(false);
    } finally {
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDeleteVideo = async () => {
    if (!window.confirm("Are you sure you want to delete this video?")) {
      return;
    }

    try {
      setUploading(true);

      // Delete from Firebase Storage if it has a path (not an external URL or embed)
      if (video.path) {
        const videoRef = ref(storage, video.path);
        await deleteObject(videoRef);
      }

      // Clear video
      onVideoUpdate(null);
      setShowModal(false);
      setEmbedUrl("");
    } catch (err) {
      setError("Failed to delete video: " + err.message);
      console.error("Delete error:", err);
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const isEmbedVideo = video?.type === "embed" || video?.embedUrl;

  return (
    <>
      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0">Property Video</h6>
          {!video && (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => setShowModal(true)}
              disabled={disabled || uploading}
            >
              {uploading ? "Uploading..." : "+ Add Video"}
            </Button>
          )}
        </Card.Header>
        <Card.Body>
          {/* Error display */}
          {error && (
            <Alert
              variant="danger"
              className="mb-3"
              dismissible
              onClose={() => setError("")}
            >
              {error}
            </Alert>
          )}

          {/* Upload progress */}
          {uploading && (
            <Alert variant="info" className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span>Uploading video...</span>
                <span>{Math.round(uploadProgress)}%</span>
              </div>
              <ProgressBar
                now={uploadProgress}
                label={`${Math.round(uploadProgress)}%`}
                animated
              />
            </Alert>
          )}

          {/* Video preview */}
          {video ? (
            <div>
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  maxWidth: "500px",
                  margin: "0 auto",
                }}
              >
                {isEmbedVideo ? (
                  <iframe
                    src={video.embedUrl || video.url}
                    width="100%"
                    height="300"
                    style={{
                      border: 0,
                      borderRadius: "8px",
                    }}
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <video
                    src={video.url}
                    controls
                    style={{
                      width: "100%",
                      borderRadius: "8px",
                      maxHeight: "300px",
                      objectFit: "contain",
                      backgroundColor: "#000",
                    }}
                  >
                    Your browser does not support the video tag.
                  </video>
                )}
              </div>

              <div className="mt-3 text-center">
                {isEmbedVideo ? (
                  <>
                    <p className="mb-2">
                      <Badge bg="info">Embed Video</Badge>
                    </p>
                    <p className="mb-2 small text-muted">
                      <strong>URL:</strong>{" "}
                      <a
                        href={video.embedUrl || video.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {(video.embedUrl || video.url).substring(0, 50)}...
                      </a>
                    </p>
                  </>
                ) : (
                  <>
                    <p className="mb-2">
                      <strong>Filename:</strong> {video.filename || "Video"}
                    </p>
                    {video.size && (
                      <p className="mb-2">
                        <strong>Size:</strong> {formatFileSize(video.size)}
                      </p>
                    )}
                  </>
                )}
                {video.uploadedAt && (
                  <p className="mb-3">
                    <strong>Added:</strong>{" "}
                    {new Date(video.uploadedAt).toLocaleDateString()}
                  </p>
                )}

                <div className="d-flex gap-2 justify-content-center">
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={handleDeleteVideo}
                    disabled={uploading}
                  >
                    Remove Video
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setShowModal(true)}
                    disabled={uploading}
                  >
                    Replace Video
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-4 text-muted">
              <p>No video added yet.</p>
              <small>
                Click "Add Video" to upload a file or add an embed link.
              </small>
            </div>
          )}

          {/* Upload instructions */}
          <div className="mt-3">
            <small className="text-muted">
              <strong>Tips:</strong>
              <br />• Upload video files up to 100MB
              <br />• Or use embed links from YouTube, Vimeo, etc.
              <br />• Video will appear in the property card carousel
              <br />• Embed format example:
              https://www.youtube.com/embed/VIDEO_ID
            </small>
          </div>
        </Card.Body>
      </Card>

      {/* Video upload/embed modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Property Video</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-3"
          >
            <Tab eventKey="upload" title="Upload File">
              <div className="p-3">
                <p className="mb-3">Upload a video file from your computer</p>

                {/* Hidden file input */}
                <Form.Control
                  ref={fileInputRef}
                  type="file"
                  accept="video/*"
                  onChange={handleFileSelect}
                  style={{ display: "none" }}
                />

                <Button
                  variant="primary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="w-100"
                >
                  {uploading ? "Uploading..." : "Choose Video File"}
                </Button>

                <div className="mt-3">
                  <small className="text-muted">
                    Supported formats: MP4, MOV, AVI, WebM
                    <br />
                    Maximum size: 100MB
                  </small>
                </div>
              </div>
            </Tab>

            <Tab eventKey="embed" title="Embed Link">
              <div className="p-3">
                <p className="mb-3">
                  Paste an embed URL or iframe code from YouTube, Vimeo, or
                  other video hosting platforms
                </p>

                <Form.Group className="mb-3">
                  <Form.Label>Embed URL or iframe code</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder='Enter embed URL like: https://www.youtube.com/embed/VIDEO_ID
or paste full iframe code like:
<iframe src="https://..." width="640" height="360"></iframe>'
                    value={embedUrl}
                    onChange={(e) => setEmbedUrl(e.target.value)}
                  />
                </Form.Group>

                <Button
                  variant="primary"
                  onClick={handleEmbedUrlSubmit}
                  disabled={!embedUrl.trim()}
                  className="w-100"
                >
                  Add Embed Video
                </Button>

                <div className="mt-3">
                  <small className="text-muted">
                    <strong>Examples:</strong>
                    <br />• YouTube:{" "}
                    <code>https://www.youtube.com/embed/VIDEO_ID</code>
                    <br />• Vimeo:{" "}
                    <code>https://player.vimeo.com/video/VIDEO_ID</code>
                    <br />• Future Home Photo:{" "}
                    <code>
                      https://listings.futurehomephoto.com/videos/VIDEO_ID
                    </code>
                  </small>
                </div>
              </div>
            </Tab>
          </Tabs>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default VideoUpload;
